import React, { useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import useWindowSize from '../../../hooks/useWindowSize';

import { getter } from '@progress/kendo-react-common';
import { cloneDeep } from 'lodash';
import {Grid, GridNoRecords, GridColumn as Column, getSelectedState} from '@progress/kendo-react-grid';
import { process as kendoProcess, orderBy, filterBy } from '@progress/kendo-data-query';
import BobWorkbookModeButton from '../../../components/BobWorkbookModeButton';
import BobTableContextMenu from '../../../components/BobTableContextMenu';
import TotalsPanel from '../../../components/TotalsPanel';
import BobActionMenu from '../../../components/BobActionMenu';
import BobLoadingMask from '../../../components/BobLoadingMask';
import BooleanCellFilter from '../../../components/CustomFilters/BooleanCellFilter';
import * as companyManagementAction from '../../../actions/companyManagementActions';
import * as dashboardAction from '../../../actions/dashboardActions';
import {calculateMinColumnWidths} from '../../../helpers/calculateMinColumnWidths';
import { kendoGridInlineStyling } from '../../../helpers/kendoGridInlineStyleHelper';
import classes from './Table.module.css';

const idGetter = getter('id');
const statusCellFilterValues = [{ text: 'Active', operator: 'eq', value: true }, { text: 'Inactive', operator: 'eq', value: false }];
const defaultItem = { text: '(All)', operator: '', value: undefined };
const CompanyEnabledCellFilter = (props, selectedBooleanFilter, setSelectedBooleanFilter) => {
    return (
        <BooleanCellFilter
            {...props}
            data={statusCellFilterValues}
            defaultItem={defaultItem}
            filterValue={selectedBooleanFilter}
            setFilterValue={setSelectedBooleanFilter}
        />
    );
};

const initialDataState = {
    take:   10,
    skip:   0,
    filter: [],
    sort:   []
};

const CompanyList = ({
    activeNavCompany,
    allCompanies,
    allCompaniesMeta,
    bobLoading,
    companyCreate,
    DisableCompany,
    EnableCompany,
    fetchCompanies,
    history,
    isActive,
    setCompanyCreate,
    setSelectedCompany,
    setWorkBookMode,
    workbookModeEnabled,
    ...props
}) => {
    const [dataState,setDataState] = useState(initialDataState);
    const [selectedState, setSelectedState] = useState({});
    const [selectedStateItems, setSelectedStateItems] = useState({});
    const [take, setTake] = useState(10);
    const [companyNameColumnSize, setCompanyNameColumnSize] = useState(300);
    const [selectedBooleanFilter, setSelectedBooleanFilter] = useState(defaultItem);

    const gridDivRef = useRef(null);
    const gridRef = useRef(null);

    const windowSize = useWindowSize();

    useEffect(() => {
        const columns = gridRef.current.props.children;
        setCompanyNameColumnSize(calculateMinColumnWidths(columns, gridDivRef.current.offsetWidth, 'name', 300, true));
    }, [windowSize]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            initialDataState.take = take;
            setDataState(initialDataState);
        }
    }, [workbookModeEnabled]);

    const onSelectionChange = (event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey:   'id',
        });

        setSelectedState(newSelectedState);

        if (event && event.dataItem) {
            let data = selectedStateItems;
            let id = event.dataItem.id;

            if (data[id]) {
                delete data[id];
            } else {
                data[id] = event.dataItem;
            }

            setSelectedStateItems(data);
        }
    };

    const onHeaderSelectionChange = event => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = cloneDeep(selectedState);
        let selectedStateItems = {};

        const filteredData = orderBy(filterBy(allCompanies, dataState.filter), dataState.sort);
        filteredData.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
            if (checked) {
                selectedStateItems[idGetter(item)] = item;
            }

        });
        if (!checked) {
            selectedStateItems = {};
        }
        setSelectedState(newSelectedState);
        setSelectedStateItems(selectedStateItems);
    };

    const [pageable] = useState({
        pageSizes:    true,
        buttonCount:  10,
        info:         true,
        previousNext: true
    });

    useEffect(() => {
        async function init() {
            fetchCompanies();
        }

        if (activeNavCompany && activeNavCompany.id) init();
    }, [activeNavCompany]);

    const handleEditCompany = companyData => {
        history.push({
            pathname: history.location.pathname,
            search:   history.location.search,
            state:    { 'returnUrl': history.location }
        });

        setSelectedCompany(companyData);
    };

    const actionsCell = props => {
        return (
            <td className={props.className} style={props.style}>
                <BobTableContextMenu
                    type="CompanyManagement"
                    entityData={props.dataItem}
                    onFetch={handleEditCompany}
                    isEnabledCompany={props.dataItem.enabled}
                    handleEnableOrDisable={() => handleEnableOrDisableCompany('ContextMenu',  props.dataItem.enabled, props.dataItem.id)}
                />
            </td>
        );
    };

    const nameCell = props => {
        return (
            <td className={props.className} style={props.style}>
                <div>
                    {props.dataItem[props.field]}
                </div>
            </td>
        );
    };

    const statusCell = props => {
        return (
            <td className={props.className} style={props.style}>
                {props.dataItem[props.field] ? 'Active' : 'Inactive'}
            </td>
        );
    };

    const handleEnableOrDisableCompany = (menuType, isActive, companyId) => {
        if ( menuType && menuType === 'ContextMenu') {
            if ( isActive ) {
                DisableCompany([companyId]);
            } else {
                EnableCompany([companyId]);
            }
        } else {
            let companyIds = [];
            for (const id in selectedStateItems) {
                companyIds.push(id);
            }
            if ( isActive ) {
                EnableCompany(companyIds);
            } else {
                DisableCompany(companyIds);
            }
        }
    };

    const refreshGrid = () => {
        const refresh = async () => {
            await fetchCompanies();
        };

        if (activeNavCompany && activeNavCompany.id) refresh();
    };

    const onDataStateChange = event => {
        setTake(event.dataState.take);
        setDataState(event.dataState);
    };

    const handleWorkbookMode = () => {
        setWorkBookMode(!workbookModeEnabled);
    };

    const BuildCompanyEnabledCellFilter = props => {
        return CompanyEnabledCellFilter(props, selectedBooleanFilter, setSelectedBooleanFilter);
    };

    return (
        <React.Fragment>
            <div ref={gridDivRef} className={classes.Table}>
                <style>
                    { kendoGridInlineStyling }
                </style>
                <div className={classes.TableContainerHead}>
                    <div className={classes.CompanyFilterContainer}>
                        <BobWorkbookModeButton
                            handleWorkbookMode={handleWorkbookMode}
                            workbookMode={workbookModeEnabled}
                        />
                    </div>
                    <div className={classes.TotalsContainer}>
                        <TotalsPanel
                            data={{
                                totalCount:    allCompanies.length,
                                totalActive:   allCompanies.filter(c => c.enabled === true).length,
                                totalInactive: allCompanies.filter(c => c.enabled === false).length
                            }}
                            entityType={'CompanyManagement'}
                        />
                    </div>
                    <div
                        className={classes.RefreshContainer}
                        aria-hidden="true"
                        role="button"
                        tabIndex={0}
                        onClick={refreshGrid}
                    >
                        <span className="material-icons-outlined">refresh</span>
                    </div>
                    <div className={classes.ActionButtonContainer}>
                        <BobActionMenu
                            type={'CompanyManagement'}
                            handleCreateCompany={() => setCompanyCreate(true)}
                            handleEnableOrDisable={(menuType, isEnable) => handleEnableOrDisableCompany(menuType, isEnable)}
                        />
                    </div>
                </div>
                <Grid
                    className={classes.KendoGrid}
                    ref={gridRef}
                    pageable={pageable}
                    sortable
                    filterable={workbookModeEnabled}
                    data={kendoProcess(allCompanies ? allCompanies.map(item => ({
                        ...item,
                        'selected': selectedState[idGetter(item)]
                    })) : [], dataState)}
                    {...dataState}
                    reorderable
                    dataItemKey={'id'}
                    selectedField={'selected'}
                    selectable={{
                        enabled: true,
                        drag:    false,
                        cell:    false,
                        mode:    'multiple',
                    }}
                    onSelectionChange={onSelectionChange}
                    onHeaderSelectionChange={onHeaderSelectionChange}
                    onDataStateChange={onDataStateChange}
                >
                    {
                        bobLoading.includes('companyManagement')  ?
                            <GridNoRecords>
                                Loading companies
                            </GridNoRecords>
                            :
                            null
                    }
                    <Column
                        field="selected"
                        width={70}
                        headerSelectionValue={
                            orderBy(filterBy(allCompanies, dataState.filter), dataState.sort).length > 0 && 
                            orderBy(filterBy(allCompanies, dataState.filter), dataState.sort).findIndex((item) => !selectedState[idGetter(item)]) === -1
                        }
                        filterable={false}
                        sortable={false}
                    />
                    <Column
                        field={'name'}
                        title={'Company'}
                        width={companyNameColumnSize}
                        cell={nameCell}
                    />
                    <Column
                        field={'applicationList'}
                        title={'Applications'}
                        width={450}
                    />
                    <Column
                        field={'enabled'}
                        title={'Status'}
                        width={250}
                        cell={statusCell}
                        filterCell={BuildCompanyEnabledCellFilter}
                    />
                    <Column
                        field=""
                        title=""
                        width={75}
                        cell={actionsCell}
                        locked={true}
                        sortable={false}
                        filterable={false}
                    />
                </Grid>
                {
                    bobLoading.includes('companyManagement') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    activeNavCompany:    state.bob.activeNavCompany,
    bobLoading:          state.bob.bobLoading,
    allCompanies:        state.companyManagement.allCompanies,
    allCompaniesMeta:    state.companyManagement.allCompaniesMeta,
    workbookModeEnabled: state.dashboard.workbookModeEnabled
});

const mapDispatchToProps = ({
    ...companyManagementAction,
    ...dashboardAction
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CompanyList));
